import { useState } from 'react'

import getSymbolFromCurrency from 'currency-symbol-map'
import { Link as RouterLink } from 'react-router-dom'
import {
  Button,
  Heading,
  HStack,
  MenuItemProps,
  Skeleton,
  Text,
} from '@chakra-ui/react'

import api from '@/api'
import { useBillingInfo, useIsAdmin, useTeam } from '@/react-query/team'
import { useUser, useUserBillingInfo } from '@/react-query/user'
import Card from '@/views/components/Card/Card'

export const Billing = () => {
  const team = useTeam()
  const isTeamAdmin = useIsAdmin()
  const [isPortalLoading, setIsPortalLoading] = useState(false)
  const billingInfo = useUserBillingInfo()
  const teamBillingInfo = useBillingInfo()
  const user = useUser()

  const renewalDate = team.data
    ? teamBillingInfo.data?.renewalDate
    : billingInfo.data?.renewalDate

  const handleOpenPortal: MenuItemProps['onClick'] = async (e) => {
    try {
      e.preventDefault()

      setIsPortalLoading(true)

      const { portalSessionUrl } = (await api.createPortalSession()).data

      window.location.href = portalSessionUrl
    } catch (error) {
      console.error(error)
      setIsPortalLoading(false)
    }
  }

  const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
  }

  const dateFormat = Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const currencySymbol = getSymbolFromCurrency(
    billingInfo.data?.currency?.toUpperCase() || 'USD',
  )

  return (
    <Card w={'100%'} p={6} px={10}>
      <Heading size={'md'}>Billing</Heading>
      {team.data ? (
        <HStack mt={6} w={'100%'} wrap={'wrap'}>
          <Text>Managed by your Team</Text>
          {isTeamAdmin && (
            <>
              &mdash;
              <Button
                as={RouterLink}
                variant={'brand-solid-white'}
                to={'/team'}
              >
                Go to Team Settings
              </Button>
            </>
          )}
        </HStack>
      ) : (
        <Skeleton
          startColor="#f5f5f5"
          endColor="#d7d7d7"
          rounded={'full'}
          isLoaded={!billingInfo.isLoading}
        >
          <HStack mt={6} spacing={16} rowGap={4} w={'100%'} wrap={'wrap'}>
            <>
              {billingInfo.data?.plan === 'FREE' ? (
                <>
                  <Text>
                    Current plan:&nbsp;&nbsp;
                    {capitalize(billingInfo.data.plan)}
                  </Text>
                  <Button
                    as={RouterLink}
                    to={'/pricing-page'}
                    variant={'brand-solid-white'}
                    flexShrink={0}
                    ml={-8}
                  >
                    Upgrade
                  </Button>
                </>
              ) : (
                <>
                  <Text>
                    Current Plan:&nbsp;&nbsp;
                    {capitalize(billingInfo.data?.plan || 'FREE')}
                    &nbsp;&nbsp;&nbsp;(
                    {currencySymbol}
                    {(billingInfo.data?.price || 0) / 100}{' '}
                    {billingInfo.data?.billingPeriod?.toLowerCase()})
                  </Text>
                  <Text>
                    Renews:{' '}
                    {dateFormat.format(
                      new Date(billingInfo.data?.renewalDate || 0),
                    )}
                  </Text>

                  <Button
                    variant={'brand-solid-white'}
                    onClick={handleOpenPortal}
                    isLoading={isPortalLoading}
                    flexShrink={0}
                  >
                    Manage subscription
                  </Button>
                </>
              )}
            </>
          </HStack>
        </Skeleton>
      )}
      <Skeleton
        startColor="#f5f5f5"
        endColor="#d7d7d7"
        rounded={'full'}
        isLoaded={!user.isLoading && (!team.data || !teamBillingInfo.isLoading)}
      >
        <HStack
          mt={6}
          pt={6}
          borderTop={'1px solid'}
          borderColor={'gray.200'}
          w={'100%'}
          wrap={'wrap'}
        >
          <Text fontWeight={500}>
            <Text as="span" fontWeight={700}>
              {user.data?.plan === 'BUSINESS'
                ? 'Unlimited'
                : user.data?.availableInterviewTranscriptions || 0}
            </Text>
            &nbsp;interview transcriptions available
            {renewalDate &&
              `, renews ${dateFormat.format(new Date(renewalDate))}`}
          </Text>
        </HStack>
      </Skeleton>
    </Card>
  )
}
